import React, { useState, useEffect } from 'react';
import { getDevPostProjects, getTimeline, getMediumArticles } from '../services/api';
import { ExternalLink, ChevronLeft, ChevronRight, Mic, BookOpen, Code, Clock, Calendar } from 'lucide-react';

const ContentShowcase = () => {
  const [projects, setProjects] = useState([]);
  const [podcasts, setPodcasts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeProject, setActiveProject] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectsData, timelineData, articlesData] = await Promise.all([
          getDevPostProjects(),
          getTimeline(),
          getMediumArticles()
        ]);
        
        setProjects(projectsData);
        
        // Extract podcasts from personal timeline
        const podcastEvent = timelineData.personal.find(e => e.podcasts && e.podcasts.length > 0);
        if (podcastEvent) {
          setPodcasts(podcastEvent.podcasts);
        }
        
        setArticles(articlesData.slice(0, 5)); // Get latest 5 articles
      } catch (error) {
        console.error('Error fetching content:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="py-24 bg-slate-50">
        <div className="container mx-auto px-6 md:px-12 text-center">
          <div className="w-16 h-16 border-4 border-slate-700 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="py-20 bg-gradient-to-b from-white via-slate-50 to-white">
      <div className="container mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 text-slate-800">
            Beyond the Day Job
          </h2>
          <p className="text-lg text-slate-600 font-light leading-relaxed">
            Projects, podcasts, and perspectives
          </p>
        </div>

        <div className="max-w-7xl mx-auto space-y-20">
          
          {/* Podcast Section - 3 Horizontal Tiles */}
          {podcasts.length > 0 && (
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center shadow-lg">
                  <Mic size={24} className="text-amber-400" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-800">Podcast Appearances</h3>
                  <p className="text-sm text-slate-500">Featured on Deep Takes Not Fakes</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {podcasts.map((podcast, index) => (
                  <a
                    key={index}
                    href={podcast.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group bg-white rounded-2xl border border-slate-200 hover:border-slate-400 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 overflow-hidden"
                  >
                    {/* Card Header with Spotify Green accent */}
                    <div className="h-2 bg-gradient-to-r from-[#1DB954] to-[#1ed760]" />
                    
                    <div className="p-6">
                      {/* Episode Number Badge */}
                      <div className="flex items-center justify-between mb-4">
                        <span className="px-3 py-1 bg-slate-100 text-slate-600 text-xs font-semibold rounded-full">
                          Episode {index + 1}
                        </span>
                        <div className="flex items-center gap-1 text-slate-400 text-xs">
                          <Clock size={12} />
                          <span>{podcast.duration}</span>
                        </div>
                      </div>
                      
                      {/* Title */}
                      <h4 className="font-bold text-slate-800 text-lg mb-3 group-hover:text-slate-900 transition-colors line-clamp-2">
                        {podcast.title}
                      </h4>
                      
                      {/* Description */}
                      <p className="text-slate-600 text-sm mb-4 line-clamp-3 leading-relaxed">
                        {podcast.description}
                      </p>
                      
                      {/* Footer */}
                      <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                        <div className="flex items-center gap-1 text-slate-400 text-xs">
                          <Calendar size={12} />
                          <span>{podcast.date}</span>
                        </div>
                        <div className="flex items-center gap-2 text-[#1DB954] font-semibold text-sm group-hover:gap-3 transition-all">
                          <span>Listen</span>
                          <ExternalLink size={14} />
                        </div>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Hackathon Projects */}
          {projects.length > 0 && (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-slate-800 rounded-xl flex items-center justify-center shadow-lg">
                  <Code size={20} className="text-amber-400 md:w-6 md:h-6" />
                </div>
                <div>
                  <h3 className="text-xl md:text-2xl font-bold text-slate-800">Hackathon Projects</h3>
                  <p className="text-xs md:text-sm text-slate-500">Built at various hackathons</p>
                </div>
              </div>

              <div className="relative">
                <div className="overflow-hidden rounded-xl md:rounded-2xl border border-slate-200 shadow-lg bg-white">
                  <div 
                    className="flex transition-transform duration-500 ease-out"
                    style={{ transform: `translateX(-${activeProject * 100}%)` }}
                  >
                    {projects.map((project) => (
                      <div key={project._id} className="min-w-full">
                        <div className="p-4 md:p-8 flex flex-col md:flex-row gap-4 md:gap-6 items-center min-h-[280px] md:min-h-[320px]">
                          <div className="w-full md:w-1/3 flex items-center justify-center bg-slate-50 rounded-lg md:rounded-xl p-4">
                            <img 
                              src={project.image} 
                              alt={project.title}
                              className="w-full h-28 md:h-40 object-contain"
                            />
                          </div>
                          <div className="w-full md:w-2/3">
                            <h4 className="text-lg md:text-xl font-bold text-slate-800 mb-2">{project.title}</h4>
                            <p className="text-slate-600 mb-3 text-xs md:text-sm leading-relaxed line-clamp-3 md:line-clamp-none">{project.description}</p>
                            <div className="flex flex-wrap gap-1.5 md:gap-2 mb-3 md:mb-4">
                              {project.technologies.slice(0, 4).map((tech, idx) => (
                                <span key={idx} className="px-2 md:px-3 py-1 bg-slate-100 text-slate-700 text-xs rounded-full font-medium">
                                  {tech}
                                </span>
                              ))}
                            </div>
                            <a
                              href={project.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-4 md:px-5 py-2 bg-slate-800 text-white font-semibold rounded-lg md:rounded-xl hover:bg-slate-700 transition-all duration-300 group text-xs md:text-sm"
                            >
                              View Project
                              <ExternalLink size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {projects.length > 1 && (
                  <>
                    <button
                      onClick={() => setActiveProject(Math.max(0, activeProject - 1))}
                      disabled={activeProject === 0}
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-slate-50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed border border-slate-200"
                    >
                      <ChevronLeft size={20} className="text-slate-600" />
                    </button>
                    <button
                      onClick={() => setActiveProject(Math.min(projects.length - 1, activeProject + 1))}
                      disabled={activeProject === projects.length - 1}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-slate-50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed border border-slate-200"
                    >
                      <ChevronRight size={20} className="text-slate-600" />
                    </button>
                    <div className="flex justify-center gap-2 mt-6">
                      {projects.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setActiveProject(idx)}
                          className={`h-2 rounded-full transition-all duration-300 ${
                            idx === activeProject ? 'bg-slate-700 w-8' : 'bg-slate-300 w-2'
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Writing Section */}
          {articles.length > 0 && (
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center shadow-lg">
                  <BookOpen size={24} className="text-amber-400" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-800">Writing</h3>
                  <p className="text-sm text-slate-500">Thoughts on Medium</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {articles.slice(0, 4).map((article, index) => (
                  <a
                    key={index}
                    href={article.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group bg-white p-6 rounded-2xl border border-slate-200 hover:border-slate-400 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                  >
                    <h4 className="font-bold text-slate-800 text-lg mb-3 line-clamp-2 group-hover:text-slate-900 transition-colors">
                      {article.title}
                    </h4>
                    <p className="text-sm text-slate-500 mb-4">
                      {new Date(article.pubDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                    <div className="flex items-center text-slate-700 font-semibold text-sm group-hover:text-slate-900">
                      <span>Read Article</span>
                      <ExternalLink size={14} className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                    </div>
                  </a>
                ))}
              </div>
              
              <div className="text-center mt-8">
                <a
                  href="https://medium.com/@nishitsadhwani"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-slate-800 text-white font-semibold rounded-xl hover:bg-slate-700 transition-all duration-300 group shadow-lg"
                >
                  View All Articles
                  <ExternalLink size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentShowcase;
